import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Image from 'next/image'
import { Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { gsap } from '@/lib/gsap'
import { heroLoad, heroRest } from '@/lib/motion'
import { useIsomorphicLayoutEffect } from '@/hooks/use-isomorphic-layout-effect'

/**
 * `null` marks the slot where the stadium-shaped counter stands in for the O,
 * the same substitution the reference makes in its own H1. Here it takes the
 * O of HOLD, so the shape lands early in the line rather than mid-word.
 */
const WORDMARK: Array<string | null> = ['H', null, 'L', 'D', ' ', 'S', 'T', 'I', 'L', 'L']

/**
 * Opening sequence.
 *
 * The composition is three photographs stacked as full-viewport layers: a
 * small one peeking from the left, another from the right, and the main
 * portrait covering both once everything lands. The side pair sits behind the
 * main layer, so they are only visible while the entrance is still running,
 * which is the whole point of the gesture.
 *
 * Each layer flies up from roughly a viewport below with its own rotation,
 * duration and delay, so they arrive as a fan rather than as a block. The
 * wordmark scales up through the tail of that, and the eyebrow, rules and
 * cue follow it. Nothing here queues: the overlaps are the choreography.
 */
export const Hero: FC = (): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useIsomorphicLayoutEffect(() => {
        const element = root.current

        if (!element) {
            return
        }

        const context = gsap.context(() => {
            const pick = <T extends Element = HTMLElement>(selector: string) =>
                element.querySelector<T>(selector)

            const layerMain = pick('[data-layer="main"]')
            const layerLeft = pick('[data-layer="left"]')
            const layerRight = pick('[data-layer="right"]')
            const mainInner = pick('[data-main-inner]')
            const word = pick('[data-hero-word]')
            const eyebrow = pick('[data-hero-eyebrow]')
            const script = pick('[data-hero-script]')
            const gradient = pick('[data-hero-gradient]')
            const rules = element.querySelectorAll<HTMLElement>('[data-hero-rule]')
            const cue = pick('[data-hero-cue]')

            if (!layerMain || !mainInner || !word) {
                return
            }

            gsap.set(element, { visibility: 'visible' })

            if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                return
            }

            const timeline = gsap.timeline()

            // The three flying layers. Same curve, deliberately different
            // distance, rotation and delay, which is what fans them out.
            type Flight = {
                from: { y: number; rotate: number }
                delay: number
                duration: number
                ease: string
            }

            const flights: Array<[Element | null, Flight]> = [
                [layerRight, heroLoad.imgRight],
                [layerLeft, heroLoad.imgLeft],
                [layerMain, heroLoad.imgMain]
            ]

            flights.forEach(([target, step]) => {
                if (!target) {
                    return
                }

                timeline.fromTo(
                    target,
                    { y: step.from.y, rotate: step.from.rotate },
                    { y: 0, rotate: 0, duration: step.duration, ease: step.ease },
                    step.delay
                )
            })

            // The main photograph's own crop, scaling up inside the wrapper
            // that is still travelling. Both land on the same frame.
            timeline.fromTo(
                mainInner,
                {
                    y: heroLoad.imgMainInner.from.y,
                    scale: heroLoad.imgMainInner.from.scale,
                    rotate: heroLoad.imgMainInner.from.rotate
                },
                {
                    y: 0,
                    scale: 1,
                    rotate: 0,
                    duration: heroLoad.imgMainInner.duration,
                    ease: heroLoad.imgMainInner.ease
                },
                heroLoad.imgMainInner.delay
            )

            timeline.fromTo(
                word,
                { y: heroLoad.word.from.y, scale: heroLoad.word.from.scale, opacity: 0 },
                {
                    y: 0,
                    scale: 1,
                    opacity: 1,
                    duration: heroLoad.word.duration,
                    ease: heroLoad.word.ease
                },
                heroLoad.word.delay
            )

            if (rules.length) {
                timeline.fromTo(
                    rules,
                    { y: heroLoad.grid.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: heroLoad.grid.duration,
                        ease: heroLoad.grid.ease,
                        stagger: 0.04
                    },
                    heroLoad.grid.delay
                )
            }

            if (gradient) {
                timeline.fromTo(
                    gradient,
                    { y: heroLoad.gradient.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 0.72,
                        duration: heroLoad.gradient.duration,
                        ease: heroLoad.gradient.ease
                    },
                    heroLoad.gradient.delay
                )
            }

            // The script line drops from above while everything else rises.
            if (script) {
                timeline.fromTo(
                    script,
                    { y: heroLoad.caption.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: heroLoad.caption.duration,
                        ease: heroLoad.caption.ease
                    },
                    heroLoad.caption.delay
                )
            }

            if (eyebrow) {
                timeline.fromTo(
                    eyebrow,
                    { y: heroLoad.eyebrow.from.y, opacity: 0 },
                    {
                        y: 0,
                        opacity: 1,
                        duration: heroLoad.eyebrow.duration,
                        ease: heroLoad.eyebrow.ease
                    },
                    heroLoad.eyebrow.delay
                )
            }

            // Last in, and the only element that arrives on a 3D flip.
            if (cue) {
                timeline.fromTo(
                    cue,
                    { rotateY: heroLoad.cue.from.rotateY, opacity: 0 },
                    {
                        rotateY: 0,
                        opacity: 1,
                        duration: heroLoad.cue.duration,
                        ease: heroLoad.cue.ease
                    },
                    heroLoad.cue.delay
                )
            }

            // Scroll-linked, and deliberately on a DIFFERENT node from the
            // load. Both used to drive scale and y on [data-main-inner]: the
            // scrub tween captured 0.4 as its start value (the load's
            // immediately-rendered from-state) and then forced the photograph
            // back to it on every scroll update, so the picture snapped to a
            // small floating crop the instant the entrance finished. Separate
            // nodes means the two can never contend for the same property.
            const mainScroll = pick('[data-main-scroll]')

            if (mainScroll) {
                gsap.to(mainScroll, {
                    yPercent: 10,
                    scale: 1.12,
                    ease: 'none',
                    scrollTrigger: {
                        trigger: element,
                        start: 'top top',
                        end: 'bottom top',
                        scrub: true
                    }
                })
            }

            gsap.to(element.querySelector('[data-hero-type]'), {
                yPercent: -38,
                opacity: 0.15,
                ease: 'none',
                scrollTrigger: {
                    trigger: element,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: true
                }
            })
        }, element)

        return () => context.revert()
    }, [])

    return (
        <section
            ref={root}
            id='hero'
            data-reveal
            className='relative h-[100svh] min-h-[38rem] w-full overflow-hidden bg-[color:var(--ink)]'
            aria-label='Introduction'
            style={{ perspective: '1200px' }}
        >
            {/* Left layer. Sits behind the main photograph, so it only reads
                while the entrance is still running. */}
            <div
                data-layer='left'
                className='absolute inset-0 -z-10 flex items-center justify-start overflow-hidden will-change-transform'
            >
                <div
                    className='absolute top-1/2 w-[30%] -translate-y-1/2 overflow-hidden rounded-sm'
                    style={{ left: '120px', aspectRatio: '1.5', rotate: `${heroRest.left}deg` }}
                >
                    <Image
                        src='/images/hero-terrace.png'
                        alt=''
                        fill
                        sizes='30vw'
                        className='object-cover'
                    />
                </div>
            </div>

            <div
                data-layer='right'
                className='absolute inset-0 -z-10 flex items-center justify-end overflow-hidden will-change-transform'
            >
                <div
                    className='absolute top-[53%] w-[32%] -translate-y-1/2 overflow-hidden rounded-sm'
                    style={{ right: '110px', aspectRatio: '1.178', rotate: `${heroRest.right}deg` }}
                >
                    <Image
                        src='/images/hero-courtyard.png'
                        alt=''
                        fill
                        sizes='32vw'
                        className='object-cover'
                    />
                </div>
            </div>

            {/* Main layer, covering the pair once everything lands. */}
            <div
                data-layer='main'
                className='absolute inset-0 overflow-hidden will-change-transform'
            >
                {/* Scroll owns this wrapper, the load owns the one inside it. */}
                <div data-main-scroll className='absolute inset-0 will-change-transform'>
                    <div
                        data-main-inner
                        className='absolute left-0 top-0 h-[117%] w-full will-change-transform'
                        style={{ filter: 'brightness(0.96)' }}
                    >
                        <Image
                            src='/images/hero-bloom.png'
                            alt='Close portrait in daylight, a pale poppy held across the face against an open sky'
                            fill
                            priority
                            sizes='100vw'
                            className='object-cover object-center'
                        />
                    </div>
                </div>
            </div>

            {/* Column rules, drawn over the photograph at a quarter opacity. */}
            <div className='pointer-events-none absolute inset-0 flex justify-between px-8 md:px-9' aria-hidden='true'>
                {[0, 1, 2, 3, 4].map((index) => (
                    <span
                        key={index}
                        data-hero-rule
                        className='block w-px bg-white/25'
                    />
                ))}
            </div>

            <div
                data-hero-gradient
                className='pointer-events-none absolute inset-0'
                aria-hidden='true'
                style={{
                    background: 'linear-gradient(transparent 30%, rgba(0,0,0,0.55) 72%, #000 100%)',
                    opacity: 0.72
                }}
            />

            <a
                href='#intro'
                data-hero-cue
                aria-label='Scroll to the introduction'
                className='absolute left-1/2 top-1/2 z-10 flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/10 text-white backdrop-blur-sm transition-[background-color,transform] duration-500 hover:scale-110 hover:bg-white/25'
            >
                <Plus className='size-5' strokeWidth={1.25} aria-hidden='true' />
            </a>

            <div
                data-hero-type
                className='absolute inset-x-0 bottom-0 z-10 flex flex-col items-center pb-5 text-white md:pb-8'
            >
                <p data-hero-eyebrow className='label mb-3 tracking-[0.22em] md:mb-4'>
                    Edinburgh · since 2017
                </p>

                <h1 className='sr-only'>Hold still a moment</h1>

                <div
                    data-hero-word
                    aria-hidden='true'
                    className='display-hero flex w-full items-center justify-center whitespace-nowrap px-2'
                >
                    {WORDMARK.map((glyph, index) => (
                        <span key={index} className='inline-block'>
                            {glyph === null ? (
                                <span
                                    className={cn(
                                        'mx-[0.04em] inline-block h-[0.62em] w-[1.34em] rounded-full',
                                        'border-[0.115em] border-current align-[-0.04em]'
                                    )}
                                />
                            ) : glyph === ' ' ? (
                                <span className='inline-block w-[0.22em]' />
                            ) : (
                                glyph
                            )}
                        </span>
                    ))}
                </div>

                <p
                    data-hero-script
                    aria-hidden='true'
                    className='script -mt-[0.3em] text-[color:var(--gold)] [font-size:clamp(2.5rem,9.5vw,9rem)] md:-mt-[0.34em]'
                >
                    a moment
                </p>
            </div>
        </section>
    )
}
